/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

const finite = z.number().refine(Number.isFinite, 'must be a finite number');

/**
 * How the scene is placed for this world.
 *
 * The scene is fitted to the device rig, never the other way round: moving a
 * device to suit the scenery would rewrite twin state and push it to a real
 * gateway (scene-switch.md §2 rule 7).
 */
export const SceneTransformSchema = z
  .object({
    position: z.object({ x: finite, y: finite, z: finite }).strict(),
    rotation_y: finite,
    scale: finite.refine((value) => value > 0, 'must be greater than zero'),
  })
  .strict();
export type SceneTransform = z.infer<typeof SceneTransformSchema>;

/**
 * Server-issued when the scene is created, like a world's id.
 *
 * There is no catalogue and nothing ships: every scene is built in the editor,
 * so a binding is either one of these or absent.
 */
export const SceneIdSchema = z.uuid();

export const PutWorldSceneRequestSchema = z
  .object({
    scene_id: SceneIdSchema,
    /** Null follows the scene's current revision; a number pins it. */
    scene_revision: z.number().int().nonnegative().nullable().optional(),
    scene_transform: SceneTransformSchema.optional(),
  })
  .strict();
export type PutWorldSceneRequest = z.infer<typeof PutWorldSceneRequestSchema>;

/**
 * A scene, built in the editor.
 *
 * Two artefacts sit behind it (devplan §3): the authoring **document**, which
 * only the editor reads, and the built **`.babylon`**, which is what a world
 * loads. `revision` names the latest built one; 0 means nothing has been
 * published yet, so the scene exists but cannot be rendered.
 */
export const SCENE_STATUSES = ['draft', 'ready', 'archived'] as const;
export const SceneStatusSchema = z.enum(SCENE_STATUSES);
export type SceneStatus = z.infer<typeof SceneStatusSchema>;

export const BoundsSchema = z
  .object({
    center: z.object({ x: finite, y: finite, z: finite }).strict(),
    radius: finite.refine((value) => value > 0, 'must be greater than zero'),
  })
  .strict();
export type Bounds = z.infer<typeof BoundsSchema>;

/** Where the device sits when the scene is first framed. */
export const DefaultViewSchema = z
  .object({ alpha: finite, beta: finite, radius: finite.refine((value) => value > 0, 'must be greater than zero') })
  .strict();
export type DefaultView = z.infer<typeof DefaultViewSchema>;

/**
 * A scene, built in the editor.
 *
 * It belongs to one world, exactly as a device does: the world is the unit of
 * access and of deletion, and a scene reached through a world is a scene every
 * member of that world may open. Two artefacts sit behind it (devplan §3): the
 * authoring **document**, which only the editor reads, and the built
 * **`.babylon`**, which is what the world loads. `revision` names the latest
 * built one; 0 means nothing has been published yet, so the scene exists but
 * cannot be rendered.
 */
export const SceneSchema = z.object({
  scene_id: SceneIdSchema,
  world_id: SceneIdSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
  status: SceneStatusSchema,
  /** The published revision, or 0 before the first publish. */
  revision: z.number().int().nonnegative(),
  /** Size of the published `.babylon`, which the loader shows before it fetches. */
  bytes: z.number().int().nonnegative(),
  /**
   * The revision whose publish carried a thumbnail, or absent if none ever did.
   *
   * The revision is part of the key rather than a timestamp beside it, so a
   * thumbnail is immutable exactly as the build it pictures is: a browser that
   * cached one is never holding a stale picture of a newer scene.
   */
  thumbnail_revision: z.number().int().positive().optional(),
  bounds: BoundsSchema.optional(),
  default_view: DefaultViewSchema.optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Scene = z.infer<typeof SceneSchema>;

export const SceneResponseSchema = z.object({ scene: SceneSchema });
export const ScenesResponseSchema = z.object({ scenes: z.array(SceneSchema) });

export const CreateSceneRequestSchema = z.object({ name: z.string().min(1).max(120) }).strict();
export type CreateSceneRequest = z.infer<typeof CreateSceneRequestSchema>;

export const UpdateSceneRequestSchema = z
  .object({ name: z.string().min(1).max(120).optional(), description: z.string().max(1000).optional() })
  .strict();
export type UpdateSceneRequest = z.infer<typeof UpdateSceneRequestSchema>;

/**
 * The authoring document, checked at its envelope and stored as written.
 *
 * The node schema itself lives in the editor, which is the only thing that
 * reads a document — pushing it down here would drag the asset catalogue and
 * its resolutions into a service that never resolves a URL. What the service
 * does own is that a document is JSON, is this version, and is small enough to
 * hand back in one response.
 */
export const SceneDocumentSchema = z.looseObject({ doc_version: z.literal(1), units: z.literal('m') });
export const SceneDocumentResponseSchema = z.object({ document: SceneDocumentSchema });

/** A scene has to load in a tab that is also running the editor (devplan §4.8). */
export const MAX_SCENE_BYTES = 100 * 1024 * 1024;

/** A document carries no textures, so anything near this is a runaway. */
export const MAX_DOCUMENT_BYTES = 1024 * 1024;

/** Set on the presigned PUT and re-checked at publish, so the two cannot drift. */
export const SCENE_CONTENT_TYPE = 'application/babylon+json';

/**
 * A thumbnail rides in the publish request rather than on a presigned URL of
 * its own: it is a few tens of kilobytes, and a second round trip to save a
 * picture nobody waits for is not worth the route.
 */
export const THUMBNAIL_CONTENT_TYPE = 'image/jpeg';

/** A 480×270 JPEG is an order of magnitude under this; anything near it is a runaway. */
export const MAX_THUMBNAIL_BYTES = 512 * 1024;

/** Base64 is 4 characters for every 3 bytes, and the body is checked before it is decoded. */
const MAX_THUMBNAIL_CHARS = Math.ceil(MAX_THUMBNAIL_BYTES / 3) * 4;

export const SceneUploadUrlRequestSchema = z
  .object({ bytes: z.number().int().positive().max(MAX_SCENE_BYTES) })
  .strict();
export type SceneUploadUrlRequest = z.infer<typeof SceneUploadUrlRequestSchema>;

export const SceneUploadUrlResponseSchema = z.object({
  /** Signed for exactly this content type and length; anything else is refused by S3. */
  url: z.string(),
  /** The revision being uploaded, which is what `publish` must be told. */
  revision: z.number().int().positive(),
  expires_at: z.string(),
});
export type SceneUploadUrlResponse = z.infer<typeof SceneUploadUrlResponseSchema>;

export const PublishSceneRequestSchema = z
  .object({
    revision: z.number().int().positive(),
    bounds: BoundsSchema.optional(),
    default_view: DefaultViewSchema.optional(),
    /** Base64 JPEG of the viewport as the author left it. Optional: a publish must not fail for want of a picture. */
    thumbnail: z.base64().max(MAX_THUMBNAIL_CHARS).optional(),
  })
  .strict();
export type PublishSceneRequest = z.infer<typeof PublishSceneRequestSchema>;

/**
 * Everything the loader needs, and one URL to fetch (scene-switch.md §2.8).
 */
export const SceneLoadResponseSchema = z.object({
  scene_id: SceneIdSchema,
  revision: z.number().int().positive(),
  url: z.string(),
  bytes: z.number().int().nonnegative(),
  bounds: BoundsSchema.optional(),
  default_view: DefaultViewSchema.optional(),
  expires_at: z.string(),
});
export type SceneLoadResponse = z.infer<typeof SceneLoadResponseSchema>;
